/**
 * Studio panels — the pieces the main screen composes.
 *
 * Split out of ResearchStudio.tsx so the orchestrator is about state and the
 * panels are about presentation. Each takes plain data and callbacks; none of
 * them reaches into engine internals.
 */
import { Fragment, ReactNode, useState } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type {
  CarriageSet, CostBreakdown, CostModel, CrossingConflict, Departure, FleetCaps,
  Network, PathUsage, PerDep, Scenario, SavedScenarioMeta, SetFormation, SetPlan,
  StockImbalance, Traction, TransferSummary,
} from './types'
import { gbp } from './engine'
import { setSeats } from './stock'
import { designateDiagrams, legLocoCodes } from './diagrams'
import { costLines } from './costs'
import { fmtMin } from './demand'
import { groupKeyFor, journeyMin, routeLabel } from './network'
import type { BacktestResult } from './backtest'
import { backtestVerdict } from './backtest'
import type { AdvisorRunLog, CandidateResult } from './advisorClient'
import { Btn, Callout, Chip, NumberField, Panel, SelectField, StatCard } from './ui'

const num0 = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })
const pct = (v: number) => `${(v * 100).toFixed(1)}%`

const SCEN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
/** '2026-07' -> 'Jul 2026'; leaves anything unexpected untouched. */
const scenarioMonthLabel = (key: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(key ?? '')
  if (!m) return key ?? ''
  const mi = Number(m[2]) - 1
  return mi >= 0 && mi < 12 ? `${SCEN_MONTHS[mi]} ${m[1]}` : key
}

// ---------------------------------------------------------------------------
//  Departure editor
// ---------------------------------------------------------------------------

export interface DepEditorProps {
  net: Network
  deps: Departure[]
  scenario: Scenario
  plan: SetPlan | null
  sets: CarriageSet[]
  locoBy: Record<string, { code: string; traction: Traction; label: string; group: string; detail?: string; pinned?: boolean }>
  chainNext: Record<string, string | null>
  subtitle?: string
  onToggle: (dep: Departure) => void
  onEdit: (dep: Departure) => void
  onPinSet: (id: string, code: string | undefined) => void
  onCycleTraction: (id: string) => void
  /** With the season roster in use (a register with engines ticked): choose the
   *  engine for this working's turn from the register — it takes the turn from
   *  this month on, for as long as it is available (Chris, 2026-09-11). */
  onPickEngine?: (id: string) => void
  onOpenService: (id: string) => void
  onAddBalancing: (dep: Departure, to: number) => void
}

export function DepEditor(p: DepEditorProps) {
  const { net, deps, scenario, plan, sets, locoBy, chainNext } = p
  const hm = (m: number) => fmtMin(m)

  const column = (dir: 'Northbound' | 'Southbound') => {
    const rows = deps.filter((d) => d.direction === dir)
    return (
      <div key={dir}>
        <div className="stu-panel-title" style={{ marginBottom: 4 }}>
          {dir} <span className="stu-panel-sub">{p.subtitle}</span>
        </div>
        {rows.length === 0 && <div className="stu-note">No departures in this direction.</div>}
        {rows.map((d) => {
          const res = scenario.perDep.find((x) => x.id === d.id)
          const load = res && res.capacity > 0 ? res.allocated / res.capacity : 0
          const group = d.group ?? groupKeyFor(net, d.o, d.d)
          const loco = locoBy[d.id]
          const setCode = plan?.byDep[d.id]
          const nextId = chainNext[d.id]
          const nd = nextId ? deps.find((x) => x.id === nextId) : undefined
          const isLastTurn = d.id in chainNext && chainNext[d.id] === null
          return (
            <div key={d.id} className={`stu-dep${d.enabled ? '' : ' stu-dep--off'}`}>
              <input
                type="checkbox"
                checked={d.enabled}
                title={d.enabled ? 'Remove this working' : 'Restore this working'}
                onChange={() => p.onToggle(d)}
              />
              <button type="button" className="stu-dep-time" title="Change this departure's time" onClick={() => p.onEdit(d)}>
                {d.time}
              </button>
              <button
                type="button"
                className="stu-dep-route"
                title={`${routeLabel(net, d.o, d.d)} — arrives ${hm(d.minutes + journeyMin(net, d.o, d.d))}. Click to change the time, origin or destination.`}
                onClick={() => p.onEdit(d)}
              >
                {routeLabel(net, d.o, d.d)} · <strong>arr {hm(d.minutes + journeyMin(net, d.o, d.d))}</strong>
              </button>
              {net.groups.length > 1 && (
                <span className={`stu-group-tag stu-group-tag--${group}`} title={net.groups.find((g) => g.key === group)?.label}>
                  {group === 'shuttle' ? 'SHT' : 'MAIN'}
                </span>
              )}
              {d.positioning && <span className="stu-group-tag stu-group-tag--pos" title="Positioning move — runs empty">ECS</span>}
              <select
                value={d.setPin ?? ''}
                title="Carriage set — Auto lets the plan choose; picking a set pins this train's whole chain to it"
                onChange={(e) => p.onPinSet(d.id, e.target.value || undefined)}
              >
                <option value="">Set: auto</option>
                {sets.filter((s) => s.active).map((s) => (
                  <option key={s.code} value={s.code}>{s.code} — {setSeats(s, plan?.formation)} seats</option>
                ))}
              </select>
              <span
                className={`stu-dep-set${setCode ? '' : ' stu-dep-set--none'}`}
                title="The set the plan assigned — its measured seats ARE this train's capacity"
              >
                {setCode
                  ? `${setCode} · ${setSeats(sets.find((s) => s.code === setCode) ?? { code: '', coaches: 0, start: 0, active: false }, plan?.formation)}`
                  : d.enabled ? 'no set!' : ''}
              </span>
              <button
                type="button"
                className={`stu-dep-loco stu-dep-loco--${loco?.traction ?? 'steam'}`}
                title={p.onPickEngine
                  ? `Engine working this turn over the month, from the season roster${loco?.detail ? ` — ${loco.detail}` : ''}. Click to choose another engine from the register: it takes this turn from this month on, for as long as it is available.`
                  : 'Engine diagram working this train. Click to switch the WHOLE diagram: auto → Diesel → Steam → auto. Running costs follow.'}
                onClick={() => (p.onPickEngine ? p.onPickEngine(d.id) : p.onCycleTraction(d.id))}
              >
                {d.enabled ? `${loco?.label ?? ''}${loco?.pinned ? ' ◆' : d.tractionPin && !p.onPickEngine ? ' •' : ''}` : ''}
              </button>
              {d.enabled && nd && (
                <span className="stu-dep-next" title={`This train then works the ${nd.time} ${routeLabel(net, nd.o, nd.d)}`}>
                  next {nd.time} → {net.stations[nd.d].name.slice(0, 4)}
                </span>
              )}
              {d.enabled && isLastTurn && (
                <span className="stu-dep-next" title="The engine's LAST turn — it ends the day here. Pick a station to add a balancing working.">
                  last turn →{' '}
                  <select value="" onChange={(e) => p.onAddBalancing(d, Number(e.target.value))}>
                    <option value="">return to…</option>
                    {net.stations.map((st, i) => (i === d.d ? null : <option key={st.code} value={i}>{st.name}</option>))}
                  </select>
                </span>
              )}
              {d.enabled && res && (() => {
                const outPct = res.capacity > 0 ? (res.outAlloc / res.capacity) * 100 : 0
                const retPct = res.capacity > 0 ? (res.retAlloc / res.capacity) * 100 : 0
                const oShare = res.allocated > 0.01 ? Math.round((res.outAlloc / res.allocated) * 100) : 0
                return (
                  <span className="stu-dep-load">
                    <span
                      className="stu-loadbar"
                      title="Green = originating (fare-paying), red = returns. Click for the capacity breakdown."
                      onClick={() => p.onOpenService(d.id)}
                    >
                      <span className="stu-loadbar-out" style={{ width: `${Math.min(100, outPct)}%` }} />
                      <span className="stu-loadbar-ret" style={{ width: `${Math.max(0, Math.min(100 - outPct, retPct))}%` }} />
                    </span>
                    <span className="stu-dep-figs">
                      <span className="stu-out">{Math.round(res.outAlloc)}</span>
                      +<span className="stu-ret">{Math.round(res.retAlloc)}</span>
                      /{res.capacity} · {Math.round(load * 100)}%
                      {res.allocated > 0.01 && <> (<span className="stu-out">{oShare}</span>/<span className="stu-ret">{100 - oShare}</span>)</>}
                    </span>
                  </span>
                )
              })()}
            </div>
          )
        })}
      </div>
    )
  }

  return <div className="stu-deps">{column('Northbound')}{column('Southbound')}</div>
}

// ---------------------------------------------------------------------------
//  Diagnostics — every constraint the model had to bend, in one place
// ---------------------------------------------------------------------------

export function DiagnosticsPanel({
  net, conflicts, imbalances, paths, transfers, costs, fitLog, lostOutbound, period,
}: {
  net: Network
  conflicts: CrossingConflict[]
  imbalances: StockImbalance[]
  paths: PathUsage[]
  transfers: TransferSummary[]
  costs: CostBreakdown
  fitLog: string[] | null
  lostOutbound: { time: string; lost: number; direction?: string }[]
  period: string
}) {
  const fatal = conflicts.filter((c) => !c.resolvable)
  const meets = conflicts.filter((c) => c.resolvable)
  const nothing =
    fatal.length === 0 && imbalances.length === 0 && costs.overCap.length === 0 &&
    !paths.some((p) => p.over) && lostOutbound.length === 0

  return (
    <>
      {nothing && (
        <Callout kind="ok" title="No constraint problems">
          No non-workable crossings, stock imbalances, path breaches or fleet overruns on this timetable.
        </Callout>
      )}

      {costs.overCap.length > 0 && (
        <Callout kind="error" title="Fleet — this timetable cannot be run as drawn">
          <ul>{costs.overCap.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </Callout>
      )}

      {paths.length > 0 && (
        <Callout
          kind={paths.some((p) => p.over) ? 'error' : 'info'}
          title="Network Rail paths"
        >
          <ul>
            {paths.map((p, i) => (
              <li key={i}>
                <strong>{p.segment}</strong> — {p.used} of {p.available} paths used ({p.traversals} traversal{p.traversals === 1 ? '' : 's'})
                {p.over ? ' — OVER BUDGET, Network Rail would not accept this' : ''}
              </li>
            ))}
          </ul>
        </Callout>
      )}

      {fatal.length > 0 && (
        <Callout kind="error" title="Single-line crossing conflicts">
          The hold needed is longer than a loop can absorb, or there is no second road there — move one of the times or short-work it:
          <ul>
            {fatal.map((c, i) => (
              <li key={i}>
                {c.aTime} {c.aRoute} and {c.bTime} {c.bRoute} {c.headOn ? 'meet head-on' : 'are in the section together'} between{' '}
                <strong>{c.section}</strong> around {fmtMin(c.atMin)} — would need a ~{Math.round(c.waitMin)}-minute hold at {c.holdAt}.
              </li>
            ))}
          </ul>
        </Callout>
      )}

      {meets.length > 0 && (
        <Callout kind="info" title="Crossing meets (normal)">
          Single-line passes the loops absorb — the later train holds briefly while the other clears:
          <ul>
            {meets.map((c, i) => (
              <li key={i}>
                {c.aTime} {c.aRoute} crosses {c.bTime} {c.bRoute} — hold ~{Math.max(1, Math.round(c.waitMin))} min at <strong>{c.holdAt}</strong> around {fmtMin(c.atMin)}.
              </li>
            ))}
          </ul>
        </Callout>
      )}

      {imbalances.length > 0 && (
        <Callout kind="warn" title="Stock balance">
          {net.stations.filter((_, i) => !net.stables.includes(i)).map((s) => s.name).join(', ')} cannot stable stock:
          a rake must arrive before it can depart, and nothing may stay overnight.
          <ul>{imbalances.map((im, i) => <li key={i}>{im.msg}</li>)}</ul>
          <span className="stu-note">★ Best fit repairs these automatically with positioning workings; fix a hand-built timetable by adding the working it names.</span>
        </Callout>
      )}

      {transfers.map((t) => (
        <Callout
          key={t.station}
          kind={t.peakTrainloads > t.platforms ? 'warn' : 'info'}
          title={`Interchange at ${net.stations[t.station].name}`}
        >
          <strong>{num0.format(Math.round(t.transferred))}</strong> passenger connections per {period}.
          Peak <strong>{num0.format(Math.round(t.peakWaiting))}</strong> people waiting at once around {fmtMin(t.peakAtMin)};
          mean wait <strong>{Math.round(t.meanWaitMin)} min</strong>;
          peak {t.peakTrainloads} trainload{t.peakTrainloads === 1 ? '' : 's'} on {t.platforms} platform{t.platforms === 1 ? '' : 's'}.
          {t.peakTrainloads > t.platforms && (
            <div style={{ marginTop: 4 }}>
              <strong>More trainloads than platforms.</strong> Spread the connections or the interchange backs up onto the running line.
            </div>
          )}
        </Callout>
      ))}

      {lostOutbound.length > 0 && (
        <Callout kind="warn" title={`Unmet outbound demand (per ${period})`}>
          <p className="stu-note" style={{ margin: '0 0 6px' }}>
            People who wanted to travel but no train within the tolerance could take them. The
            &quot;place unmet demand on the nearest departure&quot; option (Demand settings) moves them to the
            nearest earlier/later train instead of losing them.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            {(['Northbound', 'Southbound'] as const).map((dir) => {
              const rows = lostOutbound
                .filter((l) => (l.direction ?? 'Northbound') === dir)
                .map((l) => ({ time: l.time, lost: Math.round(l.lost) }))
                .sort((a, b) => a.time.localeCompare(b.time))
              if (rows.length === 0) return <div key={dir} className="stu-note">{dir}: none.</div>
              return (
                <div key={dir}>
                  <div className="stu-subhead" style={{ marginTop: 0 }}>{dir}</div>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                      <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={32} allowDecimals={false} />
                      <Tooltip formatter={(v) => [`${v} passengers`, 'unmet']} />
                      <Bar dataKey="lost" fill="#b45309" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )
            })}
          </div>
        </Callout>
      )}

      {fitLog && fitLog.length > 0 && (
        <Panel title="★ Best fit — what it did and why" collapsible defaultOpen>
          <ul className="stu-log">{fitLog.map((l, i) => <li key={i}>{l}</li>)}</ul>
        </Panel>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
//  Set roster + today's set working
// ---------------------------------------------------------------------------

export function SetRoster({
  net, sets, plan, formation, onFormation, onChange, onReset,
}: {
  net: Network
  sets: CarriageSet[]
  plan: SetPlan | null
  formation: SetFormation
  onFormation: (f: SetFormation) => void
  onChange: (sets: CarriageSet[]) => void
  onReset: () => void
}) {
  const stableOpts = net.carriageStables.map((i) => ({ value: String(i), label: net.stations[i].name }))
  const groupOpts = [{ value: '', label: 'Any route' }, ...net.groups.map((g) => ({ value: g.key, label: g.label }))]
  const numIn = (s: CarriageSet, i: number, field: 'seats' | 'seatsOffPeak' | 'coaches', title: string, width = 52) => (
    <label className="stu-note" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }} title={title}>
      <input
        type="number"
        min={0}
        value={s[field] ?? ''}
        placeholder="—"
        style={{ width }}
        onChange={(e) => {
          const v = e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))
          onChange(sets.map((x, j) => (j === i ? { ...x, [field]: field === 'coaches' ? (v ?? 0) : v } : x)))
        }}
      />
    </label>
  )
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 18 }}>
      <div>
        <div className="stu-subhead" style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          Set roster
          <span className="stu-note" style={{ fontWeight: 400 }}>
            formation:{' '}
            <button
              type="button"
              className="stu-btn stu-btn--sm"
              title="Peak or off-peak formation for the modelled day. The railway strengthens rakes for the peak (Whitby sets 407 vs 320) — one formation per day, coaches are not swapped all day."
              onClick={() => onFormation(formation === 'peak' ? 'offpeak' : 'peak')}
            >
              {formation === 'peak' ? 'Peak' : 'Off-peak'}
            </button>
          </span>
        </div>
        <p className="stu-note" style={{ margin: '0 0 8px' }}>
          A set is a fixed rake for the whole day. Chains may only be cut at the{' '}
          {net.carriageStables.map((i) => net.stations[i].name).join(' and ')} stables.
          Every train&apos;s capacity IS its assigned set&apos;s <strong>measured seats</strong>
          {' '}(peak / off-peak) — never a per-coach guess. Vehicles drive carriage costs.
        </p>
        <div className="stu-note" style={{ display: 'flex', gap: 8, margin: '0 0 4px', paddingLeft: 76 }}>
          <span style={{ width: 52 }}>peak</span><span style={{ width: 52 }}>off-peak</span><span style={{ width: 40 }}>veh</span>
        </div>
        {sets.map((s, i) => (
          <div key={i} className="stu-dep" style={{ opacity: s.active ? 1 : 0.45 }}>
            <input
              type="checkbox"
              checked={s.active}
              title="In service"
              onChange={() => onChange(sets.map((x, j) => (j === i ? { ...x, active: !x.active } : x)))}
            />
            <input
              value={s.code}
              style={{ width: 46 }}
              onChange={(e) => onChange(sets.map((x, j) => (j === i ? { ...x, code: e.target.value.toUpperCase().slice(0, 4) } : x)))}
            />
            {numIn(s, i, 'seats', 'Measured seats, PEAK formation')}
            {numIn(s, i, 'seatsOffPeak', 'Measured seats, OFF-PEAK formation (blank = same as peak)')}
            {numIn(s, i, 'coaches', 'Vehicles in the rake — drives carriage costs, NOT capacity', 40)}
            <select
              value={String(s.start)}
              title="Overnight stable"
              onChange={(e) => onChange(sets.map((x, j) => (j === i ? { ...x, start: Number(e.target.value) } : x)))}
            >
              {stableOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {net.groups.length > 1 && (
              <select
                value={s.group ?? ''}
                title="Restrict this set to one route — the shuttle unit stays on the branch"
                onChange={(e) => onChange(sets.map((x, j) => (j === i ? { ...x, group: e.target.value || undefined } : x)))}
              >
                {groupOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            <button
              type="button"
              title="Remove set"
              className="stu-btn stu-btn--sm stu-btn--quiet"
              style={{ marginLeft: 'auto' }}
              onClick={() => onChange(sets.filter((_, j) => j !== i))}
            >×</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Btn kind="primary" small onClick={() => onChange([...sets, { code: `S${sets.length + 1}`, coaches: 5, seats: 250, start: 0, active: true }])}>
            Add set
          </Btn>
          <Btn kind="quiet" small onClick={onReset}>Reset defaults</Btn>
        </div>
      </div>
      {plan && (
        <div>
          <div className="stu-subhead" style={{ marginTop: 0 }}>
            Today&apos;s set working — {plan.setsInTraffic} set{plan.setsInTraffic === 1 ? '' : 's'} in traffic, {plan.carriages} carriages out
          </div>
          {plan.chains.map((ch, i) => (
            <div key={i} className="stu-note" style={{ padding: '2px 0', color: ch.set ? undefined : '#b23b2e' }}>
              <strong style={{ color: ch.set ? '#0b3d2e' : '#b23b2e' }}>
                {ch.set ? `${ch.set.code} (${setSeats(ch.set, plan?.formation)} seats)` : 'NO SET'}
              </strong>
              {' '}— {ch.legs.map((l) => `${l.time} ${net.stations[l.o].name.slice(0, 4)}→${net.stations[l.d].name.slice(0, 4)}`).join(' · ')}
            </div>
          ))}
          {plan.warnings.map((wn, i) => (
            <div key={i} className="stu-note" style={{ color: '#b45309', marginTop: 2 }}>⚠ {wn}</div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Engine diagrams (W7) — the loco's whole day, visible
// ---------------------------------------------------------------------------

/**
 * thebigplan-style diagram list: every engine's chained day of turns, with
 * "Using X of Y" that reflects GENUINE reuse — one loco going up and down the
 * line all day, never a fresh engine magicked up per departure (the D1 rule).
 */
export function DiagramsPanel({ net, deps, dieselLoco, fleet }: {
  net: Network
  deps: Departure[]
  dieselLoco: number
  fleet: FleetCaps
}) {
  const designated = designateDiagrams(net, deps, dieselLoco, fleet.locos)
  const codes = legLocoCodes(net, deps, dieselLoco, fleet.locos)
  const used = (t: Traction) => designated.filter((x) => x.traction === t).length
  const usage = [
    { label: 'steam', used: used('steam'), of: fleet.maxSteam },
    { label: 'diesel', used: used('diesel'), of: fleet.maxDiesel },
    { label: 'railcar', used: used('railcar'), of: fleet.maxRailcar },
  ].filter((u) => u.used > 0 || u.of > 0)
  return (
    <div>
      <div className="stu-subhead" style={{ marginTop: 0 }}>
        Engine diagrams —{' '}
        {usage.map((u, i) => (
          <span key={u.label} style={{ color: u.used > u.of ? '#b23b2e' : undefined }}>
            {i > 0 ? ' · ' : ''}using {u.used} of {u.of} {u.label}
          </span>
        ))}
      </div>
      <p className="stu-note" style={{ margin: '0 0 8px' }}>
        A diagram is one engine&apos;s whole day — out and back, out and back. Trains sharing a code are the SAME
        locomotive; that reuse is what keeps the fleet honest.
      </p>
      {designated.map(({ diagram: g, traction, engine }, i) => {
        const code = codes[g.legIds[0]]?.code ?? g.code ?? `#${i + 1}`
        // A register engine prints by number and name; a turn no engine could take says so.
        const name = engine ? (engine.name ? `${engine.number} ${engine.name}` : engine.number) : (fleet.locos?.length ? 'no engine' : code)
        return (
          <div key={i} className="stu-note" style={{ padding: '3px 0', display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <strong style={{ minWidth: 44, color: !engine && fleet.locos?.length ? '#b23b2e' : traction === 'steam' ? '#0b3d2e' : traction === 'diesel' ? '#5b4a12' : '#4a3d63' }}>{name}</strong>
            <span style={{ minWidth: 52 }}>{traction}</span>
            <span style={{ minWidth: 58 }}>{g.miles.toFixed(0)} mi</span>
            <span>{g.legs.join('  →  ')}</span>
          </div>
        )
      })}
      {designated.length === 0 && <div className="stu-note">No workings — no engines needed.</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Cost breakdown
// ---------------------------------------------------------------------------

export function CostPanel({
  costs, rates, periodCosts, periodRevenue, period, runningDays, hideCards,
}: {
  costs: CostBreakdown
  rates: CostModel
  periodCosts: number
  periodRevenue: number
  period: string
  runningDays: number
  /** Results → Financial Impact shows the SEASON's boxes instead (2026-10-28). */
  hideCards?: boolean
}) {
  const lines = costLines(costs, rates)
  const dayTotal = lines.reduce((t, l) => t + l.amount, 0)
  const profit = periodRevenue - periodCosts
  return (
    <>
      {!hideCards && <div className="stu-cards">
        <StatCard label={`Revenue / ${period}`} value={gbp(periodRevenue)} sub="incl. untimed walk-up uplift" />
        <StatCard label={`Costs / ${period}`} value={gbp(periodCosts)} sub={runningDays > 1 ? `${gbp(dayTotal)}/day × ${runningDays.toFixed(1)} running days` : 'per operating day'} />
        <StatCard label={`Contribution / ${period}`} value={`${profit >= 0 ? '+' : ''}${gbp(profit)}`} tone={profit >= 0 ? 'good' : 'bad'} sub="revenue less running costs" />
        <StatCard label="Coal & CO₂e" value={`${costs.coalTonnes.toFixed(2)} t`} sub={`${costs.co2eTonnes.toFixed(1)} t CO₂e per operating day`} />
      </div>}
      <div className="stu-scroll">
        <table className="stu-table">
          <thead>
            <tr><th>Cost line</th><th>Basis</th><th className="num">£ / day</th></tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.label}>
                <td><strong>{l.label}</strong></td>
                <td className="stu-note">{l.detail}</td>
                <td className="num">{gbp(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={2}>Total per operating day</td><td className="num">{gbp(dayTotal)}</td></tr>
          </tfoot>
        </table>
      </div>
      <p className="stu-note" style={{ marginTop: 8 }}>
        This timetable needs <strong>{costs.diagrams.length}</strong> engine diagram{costs.diagrams.length === 1 ? '' : 's'} —
        an engine works out and back all day, so departures chain onto the same engine where the times allow:{' '}
        {costs.diagrams.map((g, i) => (
          <Fragment key={i}>
            <span title={g.legs.join(' · ')} style={{ borderBottom: '1px dotted #b0a89a', cursor: 'help', marginRight: 8, whiteSpace: 'nowrap' }}>
              {g.autocar ? 'Autocar' : `Diagram ${i + 1}`} ({g.legs.length} leg{g.legs.length === 1 ? '' : 's'}{g.autocar ? '' : `, ${g.maxCoaches} coaches`}, {g.miles.toFixed(0)} mi)
            </span>
          </Fragment>
        ))}
      </p>
    </>
  )
}

// ---------------------------------------------------------------------------
//  Scenarios — save, load, compare
// ---------------------------------------------------------------------------

export function ScenarioPanel({
  list, busy, error, currentId, onSave, onSaveAs, onLoad, onDelete, onCompare, compareId,
}: {
  list: SavedScenarioMeta[]
  busy: boolean
  error: string | null
  currentId: number | null
  onSave: () => void
  onSaveAs: (name: string, notes: string) => void
  onLoad: (id: number) => void
  onDelete: (id: number) => void
  onCompare: (id: number | null) => void
  compareId: number | null
}) {
  const [name, setName] = useState('')
  const [notes, setNotes] = useState('')
  return (
    <>
      {error && <Callout kind="error" title="Scenario store">{error}</Callout>}
      <div className="stu-grid stu-grid--wide" style={{ marginBottom: 10 }}>
        <label className="stu-field">
          <span className="stu-field-label">Save this design as</span>
          <input type="text" value={name} maxLength={120} placeholder="e.g. August — Grosmont shuttle, profit-optimised" onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="stu-field">
          <span className="stu-field-label">Notes (optional)</span>
          <textarea value={notes} maxLength={2000} placeholder="What this scenario is testing, and anything a reader needs to know." onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <Btn kind="primary" disabled={busy || name.trim().length === 0} onClick={() => { onSaveAs(name.trim(), notes.trim()); setName(''); setNotes('') }}>
          Save as new
        </Btn>
        <Btn kind="ghost" disabled={busy || currentId == null} onClick={onSave} title={currentId == null ? 'Load or save a scenario first' : 'Overwrite the loaded scenario'}>
          Update loaded scenario
        </Btn>
        {compareId != null && <Btn kind="quiet" onClick={() => onCompare(null)}>Clear comparison</Btn>}
      </div>

      {list.length === 0 ? (
        <p className="stu-note">Nothing saved this season yet. A saved scenario is what makes an Executive Report reproducible — its id goes in the footer.</p>
      ) : (
        <div className="stu-scroll">
          <table className="stu-table">
            <thead>
              <tr>
                <th>Name</th><th>Month</th><th>Mode</th><th>Day type</th>
                <th className="num">Revenue</th><th className="num">Costs</th><th className="num">Contribution</th>
                <th className="num">Unreturned</th><th>Saved</th><th />
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} style={s.id === currentId ? { background: '#f6f8f6' } : undefined}>
                  <td>
                    <strong>{s.name}</strong>
                    {s.id === currentId && <span className="stu-note"> · loaded</span>}
                    {s.notes && <div className="stu-note">{s.notes}</div>}
                  </td>
                  <td>{scenarioMonthLabel(s.month_key)}</td>
                  <td>{s.mode === 'grosmont' ? 'Grosmont' : 'Through'}</td>
                  <td>{s.day_class}</td>
                  <td className="num">{s.revenue != null ? gbp(s.revenue) : '—'}</td>
                  <td className="num">{s.costs != null ? gbp(s.costs) : '—'}</td>
                  <td className="num">{s.contribution != null ? gbp(s.contribution) : '—'}</td>
                  <td className="num">{s.unreturned != null ? num0.format(s.unreturned) : '—'}</td>
                  <td className="stu-note">{s.created_at?.toString().slice(0, 10)}<br />{s.created_by.split('@')[0]}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Btn small kind="ghost" disabled={busy} onClick={() => onLoad(s.id)}>Load</Btn>
                      <Btn small kind="quiet" disabled={busy} onClick={() => onCompare(s.id === compareId ? null : s.id)}>
                        {s.id === compareId ? 'Comparing' : 'Compare'}
                      </Btn>
                      <Btn small kind="danger" disabled={busy} onClick={() => onDelete(s.id)}>Delete</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/** Side-by-side A/B — the table that IS the executive case. */
export function ComparePanel({
  aLabel, bLabel, a, b, period,
}: {
  aLabel: string
  bLabel: string
  a: CandidateResult
  b: CandidateResult
  period: string
}) {
  const rows: { label: string; a: number; b: number; fmt: (v: number) => string; better: 'high' | 'low' }[] = [
    { label: `Revenue / ${period}`, a: a.revenue, b: b.revenue, fmt: gbp, better: 'high' },
    { label: `Running costs / ${period}`, a: a.costs, b: b.costs, fmt: gbp, better: 'low' },
    { label: `Contribution / ${period}`, a: a.contribution, b: b.contribution, fmt: gbp, better: 'high' },
    { label: `Passengers carried / ${period}`, a: a.carried, b: b.carried, fmt: (v) => num0.format(v), better: 'high' },
    { label: 'Turned away (lost sales)', a: a.lostSales, b: b.lostSales, fmt: (v) => num0.format(v), better: 'low' },
    { label: 'Unreturned passengers', a: a.unreturned, b: b.unreturned, fmt: (v) => num0.format(v), better: 'low' },
    { label: 'Refused to change trains', a: a.lostInterchange, b: b.lostInterchange, fmt: (v) => num0.format(v), better: 'low' },
    { label: 'Passenger connections', a: a.connected, b: b.connected, fmt: (v) => num0.format(v), better: 'high' },
    { label: 'Services run', a: a.services, b: b.services, fmt: (v) => num0.format(v), better: 'low' },
    { label: 'Steam diagrams', a: a.steamDiagrams, b: b.steamDiagrams, fmt: (v) => String(v), better: 'low' },
    { label: 'Diesel diagrams', a: a.dieselDiagrams, b: b.dieselDiagrams, fmt: (v) => String(v), better: 'low' },
    { label: 'Carriages out', a: a.carriages, b: b.carriages, fmt: (v) => String(v), better: 'low' },
    { label: 'Network Rail paths used', a: a.nrPathsUsed, b: b.nrPathsUsed, fmt: (v) => String(v), better: 'low' },
    { label: 'Coal burnt (t/day)', a: a.coalTonnes, b: b.coalTonnes, fmt: (v) => v.toFixed(2), better: 'low' },
    { label: 'CO₂e (t/day)', a: a.co2eTonnes, b: b.co2eTonnes, fmt: (v) => v.toFixed(1), better: 'low' },
  ]
  return (
    <div className="stu-scroll">
      <table className="stu-table">
        <thead>
          <tr><th>Measure</th><th className="num">{aLabel}</th><th className="num">{bLabel}</th><th className="num">Change</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const delta = r.b - r.a
            const good = r.better === 'high' ? delta > 0 : delta < 0
            const flat = Math.abs(delta) < 0.5 && Math.abs(delta) / Math.max(1, Math.abs(r.a)) < 0.005
            return (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td className="num">{r.fmt(r.a)}</td>
                <td className="num">{r.fmt(r.b)}</td>
                <td className="num" style={{ color: flat ? '#8a8a8a' : good ? '#2e7d32' : '#b23b2e', fontWeight: 700 }}>
                  {flat ? '—' : `${delta > 0 ? '+' : ''}${r.fmt(delta)}`}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Backtest / calibration
// ---------------------------------------------------------------------------

export function BacktestPanel({
  result, busy, onRun, season,
}: { result: BacktestResult | null; busy: boolean; onRun: () => void; season: string }) {
  const [showServices, setShowServices] = useState(false)
  return (
    <>
      <p className="stu-note" style={{ marginBottom: 10 }}>
        The calibration <strong>runs itself</strong> when the data loads: it replays the season&apos;s actual observed
        timetable through this engine and reconciles predicted against actual revenue, so every figure the Studio
        produces carries a measured error band rather than asking for faith.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {result && <span className="stu-note">{result.sliceCount} month/day-type slices replayed on {season}</span>}
        <Btn kind="quiet" small disabled={busy} onClick={onRun}>
          {busy ? 'Replaying…' : 'Re-run'}
        </Btn>
      </div>

      {result && (() => {
        const v = backtestVerdict(result)
        return (
          <>
            <Callout kind={v.tone} title="Verdict">{v.text}</Callout>
            <div className="stu-cards">
              <StatCard label="Mean absolute error" value={pct(result.mape)} sub="revenue-weighted across slices" tone={result.mape <= 0.1 ? 'good' : result.mape <= 0.25 ? 'warn' : 'bad'} />
              <StatCard label="Bias" value={`${result.bias >= 0 ? '+' : ''}${pct(result.bias)}`} sub={result.bias >= 0 ? 'model over-predicts' : 'model under-predicts'} />
              <StatCard label="Actual (avg day)" value={gbp(result.totalActual / Math.max(1, result.sliceCount))} sub="observed, per running day" />
              <StatCard label="Predicted (avg day)" value={gbp(result.totalPredicted / Math.max(1, result.sliceCount))} sub="engine, per running day" />
            </div>

            <div className="stu-scroll">
              <table className="stu-table">
                <thead>
                  <tr><th>Slice</th><th className="num">Running days</th><th className="num">Actual £/day</th><th className="num">Predicted £/day</th><th className="num">Error</th></tr>
                </thead>
                <tbody>
                  {result.slices.map((s) => (
                    <tr key={s.key}>
                      <td>{s.label}</td>
                      <td className="num">{s.runningDays}</td>
                      <td className="num">{gbp(s.actual)}</td>
                      <td className="num">{gbp(s.predicted)}</td>
                      <td className="num" style={{ color: Math.abs(s.error) <= 0.1 ? '#2e7d32' : Math.abs(s.error) <= 0.25 ? '#b45309' : '#b23b2e', fontWeight: 700 }}>
                        {s.error >= 0 ? '+' : ''}{pct(s.error)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 10 }}>
              <Btn kind="quiet" small onClick={() => setShowServices((v2) => !v2)}>
                {showServices ? 'Hide' : 'Show'} the worst services ({result.services.length})
              </Btn>
            </div>
            {showServices && (
              <div className="stu-scroll" style={{ marginTop: 8 }}>
                <table className="stu-table">
                  <thead>
                    <tr><th>Direction</th><th>Departure</th><th>Service</th><th className="num">Actual £/day</th><th className="num">Predicted £/day</th><th className="num">Error</th></tr>
                  </thead>
                  <tbody>
                    {result.services.slice(0, 30).map((s, i) => (
                      <tr key={i}>
                        <td>{s.direction}</td>
                        <td><strong>{s.time}</strong></td>
                        <td className="stu-note">{s.route}</td>
                        <td className="num">{gbp(s.actual)}</td>
                        <td className="num">{gbp(s.predicted)}</td>
                        <td className="num" style={{ fontWeight: 700 }}>{s.error >= 0 ? '+' : ''}{pct(s.error)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Callout kind="warn" title="What this does and does not prove">
              <ul>{result.caveats.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </Callout>
          </>
        )
      })()}
    </>
  )
}

// ---------------------------------------------------------------------------
//  Advisor
// ---------------------------------------------------------------------------

export function AdvisorPanel({
  log, running, onRun, onApply, narrative, onNarrative,
}: {
  log: AdvisorRunLog | null
  running: boolean
  onRun: () => void
  onApply: (candidateId: string) => void
  narrative: string
  onNarrative: (v: string) => void
}) {
  return (
    <>
      <p className="stu-note" style={{ marginBottom: 10 }}>
        Claude proposes candidate configurations; <strong>this browser scores every one of them with the production
        engine</strong> and hands the real numbers back. Claude never calculates, so it can only quote figures the
        engine produced. It finishes with a sensitivity pass and a draft narrative, which lands in an editable box
        below — the board paper stays yours.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Btn kind="claude" disabled={running} onClick={onRun}>
          {running ? 'Claude is working…' : 'Let Claude tune this'}
        </Btn>
        {log && <span className="stu-note">{log.rounds.length} round{log.rounds.length === 1 ? '' : 's'}, {log.rounds.reduce((t, r) => t + r.results.length, 0)} candidates scored</span>}
      </div>

      {log?.error && <Callout kind="error" title="Advisor">{log.error}</Callout>}

      {log?.recommendation && (
        <Callout kind="claude" title={log.recommendation.headline}>
          <p style={{ margin: '6px 0' }}>{log.recommendation.mechanism}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
            <Btn kind="gold" onClick={() => onApply(log.recommendation!.candidateId)}>
              Apply this configuration
            </Btn>
          </div>
          {log.recommendation.risks.length > 0 && (
            <>
              <div className="stu-callout-title" style={{ marginTop: 6 }}>Risks</div>
              <ul>{log.recommendation.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </>
          )}
          <div className="stu-callout-title" style={{ marginTop: 6 }}>Sensitivity</div>
          <p style={{ margin: '2px 0' }}>{log.recommendation.sensitivity}</p>
          <div className="stu-callout-title" style={{ marginTop: 6 }}>What would change this recommendation</div>
          <p style={{ margin: '2px 0' }}>{log.recommendation.wouldChangeMind}</p>
          {log.recommendation.assumptions.length > 0 && (
            <div className="stu-scroll" style={{ marginTop: 8 }}>
              <table className="stu-table">
                <thead><tr><th>Assumption</th><th>Value</th><th>Basis</th><th>Note</th></tr></thead>
                <tbody>
                  {log.recommendation.assumptions.map((a, i) => (
                    <tr key={i}>
                      <td><strong>{a.name}</strong></td>
                      <td>{a.value}</td>
                      <td><Chip kind={a.provenance} /></td>
                      <td className="stu-note">{a.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Callout>
      )}

      {log?.recommendation && (
        <Panel title="Executive narrative — edit before it reaches the board" collapsible defaultOpen>
          <p className="stu-note" style={{ marginTop: 0 }}>
            Claude&apos;s draft. Correct anything you disagree with; this exact text goes into the Executive Report.
          </p>
          <textarea
            value={narrative}
            onChange={(e) => onNarrative(e.target.value)}
            style={{ width: '100%', minHeight: 200, font: 'inherit', padding: 8, border: '1px solid #e5e1d6', borderRadius: 6 }}
          />
        </Panel>
      )}

      {log && log.rounds.length > 0 && (
        <Panel title="Run log — every candidate and its real numbers" collapsible defaultOpen={!log.recommendation}>
          {log.rounds.map((r) => (
            <div key={r.round} style={{ marginBottom: 12 }}>
              <div className="stu-subhead" style={{ marginTop: 0 }}>Round {r.round}</div>
              <p className="stu-note" style={{ margin: '0 0 6px' }}>{r.message}</p>
              <div className="stu-scroll">
                <table className="stu-table">
                  <thead>
                    <tr>
                      <th>Candidate</th><th>Mode</th><th className="num">Revenue</th><th className="num">Costs</th>
                      <th className="num">Contribution</th><th className="num">Carried</th><th className="num">Unreturned</th>
                      <th className="num">Paths</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.results.map((c) => {
                      const cand = r.candidates.find((x) => x.id === c.id)
                      const bad = c.fatalConflicts > 0 || c.fleetOverCap.length > 0 || c.stockIssues > 0
                      return (
                        <tr key={c.id}>
                          <td>
                            <strong>{c.id}</strong>
                            {cand && <div className="stu-note">{cand.rationale}</div>}
                          </td>
                          <td>{c.mode === 'grosmont' ? 'Grosmont' : 'Through'}</td>
                          <td className="num">{gbp(c.revenue)}</td>
                          <td className="num">{gbp(c.costs)}</td>
                          <td className="num" style={{ fontWeight: 700 }}>{gbp(c.contribution)}</td>
                          <td className="num">{num0.format(Math.round(c.carried))}</td>
                          <td className="num" style={{ color: c.unreturned > 0.5 ? '#b23b2e' : undefined }}>{num0.format(Math.round(c.unreturned))}</td>
                          <td className="num">{c.nrPathsAvailable != null ? `${c.nrPathsUsed}/${c.nrPathsAvailable}` : c.nrPathsUsed}</td>
                          <td className="stu-note" style={{ color: bad ? '#b23b2e' : '#2e7d32' }}>
                            {bad
                              ? [
                                  c.fatalConflicts > 0 ? `${c.fatalConflicts} crossing` : '',
                                  c.stockIssues > 0 ? `${c.stockIssues} stock` : '',
                                  ...c.fleetOverCap,
                                ].filter(Boolean).join('; ')
                              : 'operable'}
                          </td>
                          <td><Btn small kind="ghost" onClick={() => onApply(c.id)}>Apply</Btn></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </Panel>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
//  Fleet caps
// ---------------------------------------------------------------------------

export function FleetPanel({
  fleet, onChange, costs,
}: { fleet: FleetCaps; onChange: (f: FleetCaps) => void; costs: CostBreakdown }) {
  // With engines ticked in the Brief's register, every turn is given one of
  // them by name and the counts here are CAPPED at the ticked engines (Chris,
  // 2026-09-11: the register constrains the fleet, never adds an engine).
  const named = fleet.locos ?? []
  const fromRegister = named.length > 0
  const ticked = (t: 'steam' | 'diesel' | 'railcar') => named.filter((l) => l.traction === t).length
  return (
    <>
      <p className="stu-note" style={{ margin: '0 0 8px' }}>
        What the railway can actually put out on the day — boiler tickets, washouts, exams. Without these caps the
        optimiser will happily conjure a fourth steam engine, which is not a timetable anyone can run.
      </p>
      {fromRegister && (
        <Callout kind="info" title="Engines are named from the register">
          The {named.length} engine{named.length === 1 ? '' : 's'} ticked &ldquo;Use in simulation&rdquo; on the Cost model&apos;s Rolling
          stock in use ({named.map((l) => (l.name ? `${l.number} ${l.name}` : l.number)).join(', ')}) are given to the working
          turns by name, Whitby turns only to Whitby-enabled engines. The counts here are how many are out on a day; a count
          above the ticked engines of a traction is capped there — the register never adds an engine.
        </Callout>
      )}
      <div className="stu-grid">
        <NumberField label="Steam locos available" value={fleet.maxSteam} min={0} max={8} prov="assumed"
          hint={`Using ${costs.steamN}${fromRegister ? ` · ${ticked('steam')} ticked in the register` : ''}`} onChange={(v) => onChange({ ...fleet, maxSteam: v })} />
        <NumberField label="Diesel locos available" value={fleet.maxDiesel} min={0} max={8} prov="assumed"
          hint={`For passenger trains. Using ${costs.dieselService}${fromRegister ? ` · ${ticked('diesel')} ticked in the register` : ''}`} onChange={(v) => onChange({ ...fleet, maxDiesel: v })} />
        <NumberField label="ECS stock moves" value={fleet.maxEcsDiesel ?? 1} min={0} max={4} prov="assumed"
          hint={`Diesels for empty-stock moves only. Using ${costs.dieselEcs}${fleet.maxDiesel > costs.dieselService ? ` (an idle passenger diesel may also shunt stock)` : ''}`}
          onChange={(v) => onChange({ ...fleet, maxEcsDiesel: v })} />
        <NumberField label="Railcars available" value={fleet.maxRailcar} min={0} max={4} prov="assumed"
          hint={`Using ${costs.railcars}${fromRegister ? ` · ${ticked('railcar')} ticked in the register` : ''}`} onChange={(v) => onChange({ ...fleet, maxRailcar: v })} />
      </div>
      {costs.locoOut.some((o) => o.kind === 'no-engine') && (
        <Callout kind="error" title="A turn has no engine in the register">
          <ul>{costs.locoOut.filter((o) => o.kind === 'no-engine').map((o, i) => <li key={i}>{o.text}</li>)}</ul>
        </Callout>
      )}
      {costs.overCap.length > 0 && (
        <Callout kind="error" title="Over the fleet">
          <ul>{costs.overCap.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </Callout>
      )}
    </>
  )
}

export function PerDepTotals({ perDep }: { perDep: PerDep[] }) {
  const t = perDep.reduce(
    (a, d) => ({ cap: a.cap + d.capacity, alloc: a.alloc + d.allocated, out: a.out + d.outAlloc }),
    { cap: 0, alloc: 0, out: 0 }
  )
  return (
    <span className="stu-note">
      {perDep.length} workings · {num0.format(Math.round(t.alloc))} of {num0.format(t.cap)} seats used
      ({t.cap > 0 ? Math.round((t.alloc / t.cap) * 100) : 0}% load) · {num0.format(Math.round(t.out))} fare-paying
    </span>
  )
}

export function SelectStation({
  net, value, onChange, label,
}: { net: Network; value: number; onChange: (v: number) => void; label: string }) {
  return (
    <SelectField
      label={label}
      value={String(value)}
      options={net.stations.map((s, i) => ({ value: String(i), label: s.name }))}
      onChange={(v) => onChange(Number(v))}
    />
  )
}

export const emptyNode: ReactNode = null
